import { useCallback, useEffect, useRef } from "react";

import { AxiosError } from "axios";
import { useCompanyListStore } from "@/stores/useCompanyListStore";

import useAxiosPrivate from "../../../../../api/hooks/useAxiosPrivate";
import { ApiResources } from "../../../../../api/resources";
import { ALERT_STATUS } from "../../../../../context/alert/types";
import { sortCompanies } from "../../../../../shared/functions/functions";
import useResponse from "../../../../../shared/hooks/useResponse";

const useCompanies = (
  removeExtraApiCall?: boolean,
  noInitialLoad?: boolean,
  fromCompanySelector?: boolean // this is used to avoid redundant api call on checkbox first change
) => {
  const { handleResponse } = useResponse();
  const { getData } = useAxiosPrivate();

  const { companyList, rootCompanyList, setRootCompanyList, setCompanyList } =
    useCompanyListStore();

  const initialized = useRef(false);

  const getQueryData = useCallback(
    async (callRefreshCompanyList?: boolean, rootCompanyId?: string) => {
      // this check is created to avoid double api call in companies page on page refresh.
      const resource = ApiResources.CompanyList;
      if (
        !removeExtraApiCall ||
        (removeExtraApiCall && callRefreshCompanyList)
      ) {
        try {
          const isRootNeeded = rootCompanyId ? `/${rootCompanyId}` : "";

          const response = await getData(`${resource}${isRootNeeded}`);

          const sortedCompanies = sortCompanies(response.data.companies, true);

          const sortedRootCompanies = sortCompanies(
            response.data.rootCompanies,
            false
          );

          !rootCompanyId && setRootCompanyList(sortedRootCompanies);
          setCompanyList(sortedCompanies);
        } catch (error) {
          const err = error as AxiosError;
          if (err) {
            handleResponse(ALERT_STATUS.Critical, err.message);
          }
        } finally {
          initialized.current = true;
        }
      }
    },
    [
      getData,
      handleResponse,
      setCompanyList,
      setRootCompanyList,
      removeExtraApiCall
    ]
  );

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;

      !noInitialLoad && fromCompanySelector && getQueryData(); // fromCompanySelector is used to avoid redundant api call on checkbox first change
    }
  }, [
    getData,
    getQueryData,
    handleResponse,
    noInitialLoad,
    removeExtraApiCall,
    fromCompanySelector
  ]);

  return { companyList, rootCompanyList, getQueryData };
};

export default useCompanies;
